import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import { SERVICES } from "@/lib/services";
import useSeo from "@/lib/useSeo";

export default function InsuranceSolutionsPage() {
    useSeo({
        title: "Insurance Solutions — Personal, Business, Health & Specialty",
        description:
            "Explore Endurance Casualty's insurance solutions: home, auto, flood, life, health, Medicare, business owners, workers' comp, cyber, and specialty placements across NY, NJ, PA, CT, NC, FL, and TX.",
        canonicalPath: "/insurance-solutions",
    });

    return (
        <main
            data-testid="page-insurance-solutions"
            className="relative min-h-screen bg-brand-bg text-brand-ivory"
        >
            <PageHeader />

            <section className="mx-auto max-w-[1200px] px-6 pt-12 md:px-10 md:pt-16">
                <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                    A simple hub for every conversation
                </p>
                <h1 className="mt-4 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl md:text-6xl">
                    Insurance{" "}
                    <span className="gold-text">Solutions</span>
                </h1>
                <div className="gold-hairline my-7 w-32" />
                <p className="max-w-3xl text-base leading-relaxed text-brand-ivory/85 md:text-lg">
                    Pick the topic that fits your situation. Each page gives a short
                    plain-English overview and a simple way to request a follow-up
                    conversation with a licensed agent. Endurance Casualty provides
                    helpful general information and a simple way to begin a
                    conversation. {DISCLAIMERS.coverageGeneric}
                </p>

                <div
                    data-testid="solutions-intro-long"
                    className="mt-8 max-w-3xl space-y-4 text-sm leading-relaxed text-brand-ivory/80 sm:text-base"
                >
                    <p>
                        Endurance Casualty Insurance Agency is an independent,
                        multi-state agency licensed in New York, New Jersey,
                        Pennsylvania, Connecticut, North Carolina, Florida, and
                        Texas. Because we&apos;re independent, we quote your
                        risk across multiple admitted carriers and surplus-lines
                        markets — you get options, not a single captive product.
                    </p>
                    <p>
                        Most clients arrive with one specific question
                        (&quot;Do I need workers&apos; comp?&quot;,
                        &quot;Is my umbrella high enough?&quot;,
                        &quot;Should I move from Original Medicare to a
                        Medicare Advantage plan?&quot;) and leave with a
                        coordinated coverage map across personal, business,
                        health, and life. Pick a topic below or browse the{" "}
                        <Link
                            to="/blog"
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            Agency Blog
                        </Link>{" "}
                        for plain-English commentary, or jump straight to{" "}
                        <Link
                            to="/contact"
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            Contact
                        </Link>{" "}
                        to start a real conversation with a licensed producer.
                    </p>
                </div>
            </section>

            <section
                data-testid="solutions-grid"
                className="mx-auto max-w-[1200px] px-6 py-12 md:px-10 md:py-16"
            >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={s.path}
                            data-testid={`solutions-card-${s.slug}`}
                            className="group flex flex-col overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] transition-all hover:border-[rgba(212,175,55,0.5)] hover:shadow-[0_30px_60px_-30px_rgba(212,175,55,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                        >
                            <div className="aspect-[16/9] overflow-hidden">
                                <img
                                    src={s.image}
                                    alt=""
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                />
                            </div>
                            <div className="flex grow flex-col p-6 md:p-7">
                                <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                                    {s.kicker}
                                </p>
                                <h2 className="mt-2 font-serif text-2xl leading-tight text-brand-ivory">
                                    {s.title}
                                </h2>
                                <p className="mt-3 grow text-sm leading-relaxed text-brand-muted">
                                    {s.intro.split(". ")[0]}.
                                </p>
                                <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand-gold2">
                                    Read more
                                    <ArrowRight
                                        aria-hidden="true"
                                        className="h-3 w-3 transition-transform group-hover:translate-x-1"
                                    />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Quick contact band */}
            <section className="mx-auto max-w-[1200px] px-6 pb-16 md:px-10 md:pb-20">
                <div className="overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.3)] bg-gradient-to-r from-[#0b0e13] via-[#0d1117] to-[#0b0e13]">
                    <div className="flex flex-col items-start gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Not sure which fits?
                            </p>
                            <h3 className="mt-2 font-serif text-2xl text-brand-ivory md:text-3xl">
                                Ask a licensed agent
                            </h3>
                            <p className="mt-2 max-w-xl text-sm text-brand-muted">
                                Send a quick note and we&apos;ll route you to the right
                                conversation — or call {COMPANY.phone}.
                            </p>
                        </div>
                        <ContactDialog
                            defaultMessage="I'd like help figuring out which topic fits my situation."
                            trigger={
                                <button
                                    type="button"
                                    data-testid="solutions-contact-cta"
                                    className="enc-submit inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm focus-visible:ring-2 focus-visible:ring-brand-gold"
                                >
                                    Request a follow-up
                                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                                </button>
                            }
                        />
                    </div>
                </div>
            </section>

            <FooterBar />
        </main>
    );
}
